import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import AddCircleIcon from '@mui/icons-material/AddCircle';

import OnDeleteIcon from '../../../icons/OnDeleteIcon';
import { uuid } from '../../../utils/stringUtils';
import { hasRole } from '../../../utils/userRoles';
import SingleSelect from '../../formElements/SingleSelect';
import Switch from '../../formElements/Switch';
import TextInputField from '../../formElements/TextInputField';
import ScreenError from '../../ui/messageBanners/ScreenError';

import local from './appointmentsAdmin.module.css';

const AutomaticAppointment = ({
    acceptedRoles,
    roles,
    appointment,
    contractAppointmentTypes,
    onUpdateAutomaticAppointment,
    errors,
    clearError
}) => {
    // LOCAL STATE
    const [rows, setRows] = useState([]);

    // USEEFFECTS
    useEffect(() => {
        if ('triggers' in appointment) {
            if (appointment.active) setRows(appointment.triggers);
            else {
                const updatedTriggers = appointment.triggers.map((el) => ({
                    ...el,
                    active: false
                }));
                setRows(updatedTriggers);
                onUpdateAutomaticAppointment({ ...appointment, triggers: updatedTriggers });
            }
        }
    }, [appointment]);

    // EVENT HANDLERS
    const onAddRow = () => {
        clearError(`automaticAppointment-${appointment.id}`);
        clearError(`accordion-${appointment.id}`);
        const newRow = { id: `new-${uuid()}`, active: true };
        const updatedRows = [...rows, newRow];
        setRows(updatedRows);
        onUpdateAutomaticAppointment({ ...appointment, triggers: updatedRows });
    };

    const onUpdate = (rowId, key, value, errorKey) => {
        clearError(errorKey);
        clearError(`accordion-${appointment.id}`);
        const updatedRows = rows.map((el) => (el.id === rowId ? { ...el, [key]: value } : el));
        setRows(updatedRows);
        onUpdateAutomaticAppointment({ ...appointment, triggers: updatedRows });
    };

    const onDeleteRow = (rowId, errorKeys) => {
        errorKeys.forEach((el) => clearError(el));
        clearError(`accordion-${appointment.id}`);
        const updatedRows = rows.filter((el) => el.id !== rowId);
        setRows(updatedRows);
        onUpdateAutomaticAppointment({ ...appointment, triggers: updatedRows });
    };

    // RENDER
    return (
        <div className={local.triggerRows}>
            {rows.length > 0 &&
                rows.map((el) => (
                    <div key={el.id} className={local.triggerRow} data-testid={`row_${el.id}`}>
                        <SingleSelect
                            id={`triggerAppointmentTypeId-${el.id}`}
                            label="Trigger Appointment Type"
                            placeholder={'Select trigger appointment type...'}
                            disabled={!hasRole(acceptedRoles, roles) || !appointment.active}
                            mandatory={true}
                            menuItems={contractAppointmentTypes || []}
                            selectedId={el.triggerAppointmentTypeId || ''}
                            selected={
                                contractAppointmentTypes?.find(
                                    (entry) => entry.id === el.triggerAppointmentTypeId
                                ) || {}
                            }
                            error={
                                errors[
                                    `automaticAppointment-${appointment.id}-triggerAppointmentTypeId-${el.id}`
                                ]
                            }
                            onChange={(chosenId) =>
                                onUpdate(
                                    el.id,
                                    'triggerAppointmentTypeId',
                                    chosenId,
                                    `automaticAppointment-${appointment.id}-triggerAppointmentTypeId-${el.id}`
                                )
                            }
                        />
                        <TextInputField
                            id={`triggerAfterDays-${el.id}`}
                            label="Days After Trigger to Book"
                            placeholder="Enter number of days..."
                            disabled={!hasRole(acceptedRoles, roles) || !appointment.active}
                            mandatory={true}
                            maxLength={3}
                            value={el.triggerAfterDays || ''}
                            error={
                                errors[
                                    `automaticAppointment-${appointment.id}-triggerAfterDays-${el.id}`
                                ]
                            }
                            onChange={(e) =>
                                onUpdate(
                                    el.id,
                                    'triggerAfterDays',
                                    e.target.value.replace(/[^\d]/g, ''),
                                    `automaticAppointment-${appointment.id}-triggerAfterDays-${el.id}`
                                )
                            }
                        />
                        <div className={local.deleteIcon}>
                            <OnDeleteIcon
                                onDelete={() =>
                                    onDeleteRow(el.id, [
                                        `automaticAppointment-${appointment.id}-triggerAppointmentTypeId-${el.id}`,
                                        `automaticAppointment-${appointment.id}-triggerAfterDays-${el.id}`
                                    ])
                                }
                                roles={roles}
                                acceptedRoles={acceptedRoles}
                                active={hasRole(acceptedRoles, roles) && el.id.startsWith('new-')}
                                customClass="largeIcon"
                            />
                        </div>
                        <div className={local.switch}>
                            {!el.id.startsWith('new-') && (
                                <Switch
                                    id={`active-${el.id}`}
                                    label="Active"
                                    checked={el.active}
                                    disabled={!hasRole(acceptedRoles, roles) || !appointment.active}
                                    onOff={true}
                                    customClass="rowSwitch"
                                    onChange={(e) => onUpdate(el.id, 'active', e.target.checked)}
                                />
                            )}
                        </div>
                    </div>
                ))}

            <div
                className={`${local.addTriggerButton} ${!appointment.active ? local.disabledIcon : ''}`}
                onClick={appointment.active ? onAddRow : null}>
                <AddCircleIcon />
                <span> Add New Trigger </span>
            </div>

            {errors[`automaticAppointment-${appointment.id}`]?.error && (
                <ScreenError
                    error={errors[`automaticAppointment-${appointment.id}`]}
                    name={`automaticAppointment-${appointment.id}`}
                    customClass="alignCenter"
                    allowDismiss={false}
                />
            )}
        </div>
    );
};

AutomaticAppointment.propTypes = {
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    roles: PropTypes.arrayOf(PropTypes.string),
    appointment: PropTypes.object,
    contractAppointmentTypes: PropTypes.arrayOf(PropTypes.object),
    onUpdateAutomaticAppointment: PropTypes.func,
    errors: PropTypes.object,
    clearError: PropTypes.func
};

export default AutomaticAppointment;
